import React, { useEffect, useState, useMemo } from 'react';
import { RadioButtonGroup } from '@agconnections/grow-ui';
import {
  Button,
  ButtonType,
  Select
} from 'syngenta-digital-cropwise-react-ui-kit';

import { useFormikContext } from 'formik';
import {
  getAbbreviatedUnit,
  getShortUnitLabel
} from 'helpers/unitsAbbreviation';
import OpenIcon from 'assets/open_in_new.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import ChevronUp from 'assets/chevron_up.svg?react';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import getDropdownParentWithoutDetaching from 'screens/Applications&WorkOrders/StepsLayout/Helpers/getDropdownParentWithoutDetaching';
import useInventoryDocumentationData from 'screens/ProductList/ProductDetails/hooks/useInventoryDocumentationData';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import InputWithPreferenceBlur from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/components/ProductQuantitySection/components/InputWithPreferenceBlur';
import { QUANTITY_MODES } from '../../constants';
import { ProductUnit } from '../../types';

import FormSectionHeader from '../FormSectionHeader';
import { useProductFormUnits } from '../../hooks/useProductFormUnits';
import { useProductCalculations } from '../../hooks/useProductCalculations';

interface Props {
  product: Product;
  className?: string;
  application?: FormValues;
}

type UnitFields = 'ratePerAreaUnit' | 'totalProductUnit' | 'ratePerTankUnit';

const ChevronIcon = ({ visible }: { visible: boolean }) => {
  const Component = visible ? ChevronUp : ChevronDown;
  return <Component width={18} height={18} />;
};

const setSpacingClassNames = (isActive: boolean) =>
  isActive ? 'mb-6' : 'mb-8';

const renderUnitSelect = (
  fieldName: UnitFields,
  value: string,
  onDropdownVisibleChange: (visible: boolean) => void,
  isDropdownVisible: boolean,
  units: ProductUnit[],
  onChange: (value: string) => void,
  testId: string
) => (
  <Select
    showSearch
    placeholder={fieldName === 'totalProductUnit' ? 'Gallon' : undefined}
    value={value}
    onChange={onChange}
    className="custom-select"
    data-testid={testId}
    suffixIcon={<ChevronIcon visible={isDropdownVisible} />}
    onDropdownVisibleChange={onDropdownVisibleChange}
    getPopupContainer={getDropdownParentWithoutDetaching}
    filterOption={filterOptionByNormalizedString}
  >
    {units.map(unit => (
      <Select.Option
        key={`${fieldName}-${unit.key}`}
        value={unit.key}
        label={getShortUnitLabel(unit.key)}
      >
        {getShortUnitLabel(unit.key)}
      </Select.Option>
    ))}
  </Select>
);

const ProductQuantitySection: React.FC<Props> = ({
  product,
  className = 'mb-6',
  application
}) => {
  const units = useProductFormUnits(product);
  const [isDropdownVisiblePerArea, setIsDropdownVisiblePerArea] = useState(
    false
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isDropdownVisiblePerTank, setIsDropdownVisiblePerTank] = useState(
    false
  );

  const { isTankInfoEnabled, tankCount } = application || {};

  const {
    onRatePerAcreCalculation,
    onTotalProductCalculation,
    onRatePerTankCalculation
  } = useProductCalculations({ tankCount, isTankInfoEnabled });

  const { values: productToAdd, setFieldValue } = useFormikContext<
    Product & { _preferenceData?: any }
  >();

  const { quantityMode } = productToAdd;

  // Fetch product documentation to get the label document
  const { dataDocumentation, openFile } = useInventoryDocumentationData(
    product.id
  );

  // Extract documentdescriptions for dependency tracking
  const documentDescriptions = (dataDocumentation as any)?.documentdescriptions;

  // Find the label document from the documentation array
  const labelDocument = useMemo(() => {
    if (!dataDocumentation) return null;

    // dataDocumentation is the product object with documentdescriptions array
    if (
      !documentDescriptions ||
      !Array.isArray(documentDescriptions) ||
      documentDescriptions.length === 0
    ) {
      return null;
    }

    return (
      documentDescriptions.find((doc: any) => doc.description === 'Label') ||
      null
    );
  }, [dataDocumentation, documentDescriptions]);

  const handleQuantityModeChange = (mode: string) => {
    // store the selected mode in session storage
    sessionStorage.setItem('productQuantityMode', mode);
    setFieldValue('quantityMode', mode);
  };

  const handleTotalUnitChange = (unit: string, changed: UnitFields) => {
    const baseArgs = [
      productToAdd.totalProduct || 0,
      productToAdd.appliedAreaValue
    ] as const;

    switch (changed) {
      case 'totalProductUnit': {
        const result = onTotalProductCalculation(...baseArgs, unit);

        if (!result) return;

        setFieldValue('ratePerAcre', result.ratePerAreaValue);
        setFieldValue('ratePerTank', result.ratePerTankValue);
        break;
      }
      case 'ratePerAreaUnit': {
        const result = onTotalProductCalculation(
          ...baseArgs,
          productToAdd.totalProductUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            ratePerAreaUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('ratePerAcre', result.ratePerAreaValue);
        break;
      }
      case 'ratePerTankUnit': {
        const result = onTotalProductCalculation(
          ...baseArgs,
          productToAdd.totalProductUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            ratePerTankUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('ratePerTank', result.ratePerTankValue);
        break;
      }
      default:
        break;
    }
  };

  const handleRatePerAcreUnitChange = (unit: string, changed: UnitFields) => {
    const baseArgs = [
      productToAdd.ratePerAcre || 0,
      productToAdd.appliedAreaValue
    ] as const;

    switch (changed) {
      case 'ratePerAreaUnit': {
        const result = onRatePerAcreCalculation(...baseArgs, unit);

        if (!result) return;

        setFieldValue('totalProduct', result.totalProductValue);
        setFieldValue('ratePerTank', result.ratePerTankValue);
        break;
      }
      case 'totalProductUnit': {
        const result = onRatePerAcreCalculation(
          ...baseArgs,
          productToAdd.ratePerAreaUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            totalProductUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('totalProduct', result.totalProductValue);
        break;
      }
      case 'ratePerTankUnit': {
        const result = onRatePerAcreCalculation(
          ...baseArgs,
          productToAdd.ratePerAreaUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            ratePerTankUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('ratePerTank', result.ratePerTankValue);
        break;
      }
      default:
        break;
    }
  };

  const handleRatePerTankUnitChange = (unit: string, changed: UnitFields) => {
    const baseArgs = [
      productToAdd.ratePerTank || 0,
      productToAdd.appliedAreaValue
    ] as const;

    switch (changed) {
      case 'ratePerTankUnit': {
        const result = onRatePerTankCalculation(...baseArgs, unit);

        if (!result) return;

        setFieldValue('totalProduct', result.totalProductValue);
        setFieldValue('ratePerAcre', result.ratePerAreaValue);
        break;
      }
      case 'totalProductUnit': {
        const result = onRatePerTankCalculation(
          ...baseArgs,
          productToAdd.ratePerTankUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            totalProductUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('totalProduct', result.totalProductValue);
        break;
      }
      case 'ratePerAreaUnit': {
        const result = onRatePerTankCalculation(
          ...baseArgs,
          productToAdd.ratePerTankUnit,
          {
            ...productToAdd,
            // we will ALWAYS convert the corresponding quantity to the new UOM.
            ratePerAreaUnit: unit
          }
        );

        if (!result) return;

        setFieldValue('ratePerAcre', result.ratePerAreaValue);
        break;
      }
      default:
        break;
    }
  };

  const handleUnitChange = (unit: string, changed: UnitFields) => {
    setFieldValue(changed, unit);

    const handlers = {
      [QUANTITY_MODES.RATE_PER_ACRE]: handleRatePerAcreUnitChange,
      [QUANTITY_MODES.TOTAL_PRODUCT]: handleTotalUnitChange,
      [QUANTITY_MODES.RATE_PER_TANK]: handleRatePerTankUnitChange
    };

    if (!quantityMode || !handlers[quantityMode]) return;

    handlers[quantityMode](unit, changed);
  };

  useEffect(() => {
    const input = document.querySelector(
      `input#${quantityMode}`
    ) as HTMLInputElement;

    if (input) {
      setTimeout(() => {
        input.focus();
      }, 100);
    }
  }, [product.isInUse, quantityMode]);

  useEffect(() => {
    if (
      !isTankInfoEnabled &&
      productToAdd.quantityMode === QUANTITY_MODES.RATE_PER_TANK
    ) {
      setFieldValue('quantityMode', QUANTITY_MODES.RATE_PER_ACRE);
    }

    if (isTankInfoEnabled || !productToAdd.ratePerTank) {
      return;
    }

    setFieldValue('ratePerTank', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTankInfoEnabled, productToAdd.quantityMode, productToAdd.ratePerTank]);

  const renderRatePerAcreField = (isActive: boolean) => (
    <div className={setSpacingClassNames(isActive)}>
      <InputWithPreferenceBlur
        label="Rate per Acre"
        id={QUANTITY_MODES.RATE_PER_ACRE}
        fieldName="ratePerAcre"
        isRequired={isActive}
        disabled={!isActive}
        onControlledBlur={onRatePerAcreCalculation}
        inputSuffix={`${getAbbreviatedUnit(productToAdd.ratePerAreaUnit)}/ac`}
        addonAfter={renderUnitSelect(
          'ratePerAreaUnit',
          productToAdd.ratePerAreaUnit,
          setIsDropdownVisiblePerArea,
          isDropdownVisiblePerArea,
          units,
          (value: string) => handleUnitChange(value, 'ratePerAreaUnit'),
          'select-ratePerAreaUnit'
        )}
      />
    </div>
  );

  const renderRatePerTankField = (isActive: boolean) => (
    <div className={setSpacingClassNames(isActive)}>
      <FormInputWithPrecisionDisplay
        label="Rate per Tank"
        id={QUANTITY_MODES.RATE_PER_TANK}
        fieldName="ratePerTank"
        isRequired={isActive}
        disabled={!isActive}
        onControlledBlur={onRatePerTankCalculation}
        inputSuffix={`${getAbbreviatedUnit(productToAdd.ratePerTankUnit)}/tank`}
        addonAfter={renderUnitSelect(
          'ratePerTankUnit',
          productToAdd.ratePerTankUnit ?? '',
          setIsDropdownVisiblePerTank,
          isDropdownVisiblePerTank,
          units,
          (value: string) => handleUnitChange(value, 'ratePerTankUnit'),
          'select-ratePerTankUnit'
        )}
      />
    </div>
  );

  const renderTotalProductField = (isActive: boolean) => (
    <div className={setSpacingClassNames(isActive)}>
      <FormInputWithPrecisionDisplay
        label="Total Product"
        id={QUANTITY_MODES.TOTAL_PRODUCT}
        fieldName="totalProduct"
        isRequired={isActive}
        disabled={!isActive}
        onControlledBlur={onTotalProductCalculation}
        inputSuffix={getAbbreviatedUnit(productToAdd.totalProductUnit)}
        addonAfter={renderUnitSelect(
          'totalProductUnit',
          productToAdd.totalProductUnit,
          setIsDropdownVisible,
          isDropdownVisible,
          units,
          (value: string) => handleUnitChange(value, 'totalProductUnit'),
          'select-totalProductUnit'
        )}
      />
    </div>
  );

  return (
    <section data-testid="product-quantity-section" className={className}>
      <FormSectionHeader title="Product Quantity" />

      <RadioButtonGroup
        id="product-quantity-mode"
        selected={quantityMode}
        onChange={handleQuantityModeChange}
      >
        <RadioButtonGroup.RadioButton
          name="quantityMode"
          value={QUANTITY_MODES.RATE_PER_ACRE}
        >
          <span className="text-center w-full">Rate per Acre</span>
        </RadioButtonGroup.RadioButton>
        {isTankInfoEnabled ? (
          <RadioButtonGroup.RadioButton
            name="quantityMode"
            value={QUANTITY_MODES.RATE_PER_TANK}
          >
            <span className="text-center w-full">Rate per Tank</span>
          </RadioButtonGroup.RadioButton>
        ) : (
          <></>
        )}
        <RadioButtonGroup.RadioButton
          name="quantityMode"
          value={QUANTITY_MODES.TOTAL_PRODUCT}
        >
          <span className="text-center w-full">Total Product</span>
        </RadioButtonGroup.RadioButton>
      </RadioButtonGroup>
      <div className="flex flex-col mt-4">
        {/* Rate per Acre Mode: per acre, per tank, total product */}
        {quantityMode === QUANTITY_MODES.RATE_PER_ACRE && (
          <>
            {renderRatePerAcreField(true)}
            {isTankInfoEnabled && renderRatePerTankField(false)}
            {renderTotalProductField(false)}
          </>
        )}

        {/* Rate per Tank Mode: per tank, per acre, total product */}
        {quantityMode === QUANTITY_MODES.RATE_PER_TANK && isTankInfoEnabled && (
          <>
            {renderRatePerTankField(true)}
            {renderRatePerAcreField(false)}
            {renderTotalProductField(false)}
          </>
        )}

        {/* Total Product Mode: total product, per acre, per tank */}
        {quantityMode === QUANTITY_MODES.TOTAL_PRODUCT && (
          <>
            {renderTotalProductField(true)}
            {renderRatePerAcreField(false)}
            {isTankInfoEnabled && renderRatePerTankField(false)}
          </>
        )}
      </div>

      {labelDocument && (
        <Button
          className="w-full flex justify-center items-center"
          type={ButtonType.primary}
          icon={<OpenIcon />}
          ghost
          onClick={() => openFile(labelDocument.id)}
        >
          Open Product Label
        </Button>
      )}
    </section>
  );
};

export default ProductQuantitySection;
